import { DeviceType, HALFrameType, IHALInterface, ISimulatorDevice } from "./Base";
import { MobileDevice } from "./WIFI";
import { BluetoothDevice, BluetoothInfo, BluetoothMasterDevice, BLUETHOOTH_SERVICE } from "./Bluethooth";
import { generateRandomMac } from "../../../tools";
import { InfraredRayCommand, InfraredRayDevice, InfraredRayDeviceRecord } from "./InfraredRay";
import { Node } from "@antv/x6";

export enum IOT_EVENT_LEVEL {
    CODE_RED = 'red',
    CODE_ORANGE = 'orange',
    CODE_GREEN = 'green',
}

export interface IOT_EVNET {
    when: Date,
    level: IOT_EVENT_LEVEL,
    message: string,
}

// 智能音箱
export class SmartSpeakerDevice implements ISimulatorDevice {
    protected node: Node;
    protected mobileDevice: MobileDevice;
    protected bluethoothGateway: BluetoothMasterDevice;
    protected infraredRaySender: InfraredRayDevice;
    protected bridge: any = null;

    // 红外信号记录数组
    protected infraredSignalRecords: Array<InfraredRayDeviceRecord> = [];

    protected events: IOT_EVNET[] = [];

    constructor(node: Node) {
        this.node = node;
        this.mobileDevice = new MobileDevice(node);

        this.bluethoothGateway = new BluetoothMasterDevice(node,
            generateRandomMac(),
            [],
            ["制造商：友谱", "型号：YOPU-001", "版本：1.0.0"]
        , this.HAL_Recv, this.addEvent, this.getEvents);

        this.infraredRaySender = new InfraredRayDevice(node);
        
        this.addEvent = this.addEvent.bind(this);
    }

    addEvent(level: IOT_EVENT_LEVEL ,message: string) {
        this.events.unshift({
            when: new Date(),
            level,
            message,
        });
    }

    getEvents(): IOT_EVNET[] {
        return this.events;
    }

    getNode(): Node<Node.Properties> {
        return this.node;
    }

    // 扫描蓝牙设备，只有处于配网模式的蓝牙设备可以被扫描到
    scanBluetoothDevice(): BluetoothDevice[] {
        return this.bluethoothGateway.scan();
    }

    // 获取已配对的蓝牙设备
    getPairedBluetoothDevice(): BluetoothInfo[] {
        return this.bluethoothGateway.pairedDeviceRecords;
    }

    // 获取红外设备记录
    getInfraredSignalRecords(): Array<InfraredRayDeviceRecord> {
        return this.infraredSignalRecords;
    }

    // 增加红外设备记录
    addInfraredSignalRecord(deviceType: DeviceType, deviceName: string): void {
        // 检查是否有重复数据
        for (let i = 0; i < this.infraredSignalRecords.length; i++) {
            if (this.infraredSignalRecords[i].deviceType === deviceType && this.infraredSignalRecords[i].deviceName === deviceName) {
                // 抛出异常
                throw new Error("重复的红外设备记录");
            }
        }

        this.infraredSignalRecords.push({
            deviceType,
            deviceName,
        });

        // 通知界面更新
        this.bridge.onGraphChange();
    }

    // 删除红外设备记录
    removeInfraredSignalRecord(deviceType: DeviceType, name: string): void {
        for (let i = 0; i < this.infraredSignalRecords.length; i++) {
            if (this.infraredSignalRecords[i].deviceType === deviceType && this.infraredSignalRecords[i].deviceName === name) {
                this.infraredSignalRecords.splice(i, 1);

                // 通知界面更新
                this.bridge.onGraphChange();
                return;
            }
        }

        // 抛出异常
        throw new Error("没有找到对应的红外设备记录");
    }

    // 发送蓝牙指令
    // 例如：发送蓝牙指令，打开门锁
    // sendBluetoothCommand(device, BluetoothService.SMART_LOCK_UNLOCK)
    sendBluetoothCommand(device: BluetoothInfo, command: BLUETHOOTH_SERVICE, data: any, callback = console.log): void {
        this.bluethoothGateway.send(device, command, data, callback);
    }

    // 发送红外线指令
    // 场景内所有红外设备都会收到指令
    // 例如：发送红外线指令，打开投影机
    // sendInfraredRayCommand(InfraredRayCommand.ProjectorDevicePowerOn)
    // 例如：发送红外指令，打开空调
    // sendInfraredRayCommand(InfraredRayCommand.AirConditionerSetting, {power: true, temperature: 25, mode: "制冷", windSpeed: 3})
    sendInfraredRayCommand(command: InfraredRayCommand, config: Object | null = null, callback = console.log): void {
        const message = {
            ...config,
            command,
        };

        this.infraredRaySender.send(message, callback);
    }

    get mobileDeviceInstance() {
        return this.mobileDevice;
    }

    setBridge(bridge: any): void {
        this.mobileDevice.setBridge(bridge);
        this.bluethoothGateway.setBridge(bridge);
        this.infraredRaySender.setBridge(bridge);
        this.bridge = bridge;
    }

    getPorts(): IHALInterface[] {
        return this.mobileDevice.getPorts();
    }

    destory() {
        this.mobileDevice.destory();
        this.bluethoothGateway.destory();
        this.infraredRaySender.destory();
    }

    get deviceType() {
        return DeviceType.SmartSpeaker;
    }

    toJSON() {
        return {
            deviceType: DeviceType.SmartSpeaker,
            mobileDeviceConfig: this.mobileDevice.toJSON(),
            bluethoothGatewayConfig: this.bluethoothGateway.toJSON(),
            infraredSignalRecords: this.infraredSignalRecords,
        };
    }

    fromJSON(config: any): void {
        this.mobileDevice.fromJSON(config.mobileDeviceConfig);
        this.bluethoothGateway.fromJSON(config.bluethoothGatewayConfig);
        this.infraredSignalRecords = config.infraredSignalRecords;
    }

    // 处理蓝牙消息
    HAL_Recv(fromInterface: IHALInterface, fromMAC: string, toMAC: string, type: HALFrameType, data: any, callback: Function): void {
        // 确定是蓝牙消息帧
        if (type !== HALFrameType.Bluetooth) {
            throw new Error('不是蓝牙消息帧');
        }

        // 根据消息数据的服务类型，分别处理
        switch (data.service) {
            case BLUETHOOTH_SERVICE.DOOR_BELL:
                // 新建Audio标签播放门铃声音，播完删除
                const audio = new Audio(require('@/assets/10101.mp3'));
                audio.play();
                audio.onended = () => {
                    audio.remove();
                };

                this.addEvent(IOT_EVENT_LEVEL.CODE_ORANGE, '门铃响了');

                break;
            // 智能锁
            case BLUETHOOTH_SERVICE.SMART_LOCK_REGISTER:
                this.addEvent(IOT_EVENT_LEVEL.CODE_GREEN, '智能锁注册认证记录，模式为' + data.type);
                break;
            case BLUETHOOTH_SERVICE.SMART_LOCK_LOCK:
                this.addEvent(IOT_EVENT_LEVEL.CODE_GREEN, '智能锁已上锁');
                break;
            case BLUETHOOTH_SERVICE.SMART_LOCK_UNLOCK:
                this.addEvent(IOT_EVENT_LEVEL.CODE_GREEN, '智能锁已解锁');
                break;
            case BLUETHOOTH_SERVICE.SMART_LOCK_AUTH:
                this.addEvent(data.result ? IOT_EVENT_LEVEL.CODE_GREEN: IOT_EVENT_LEVEL.CODE_RED, `智能锁认证${data.result ? '成功': '失败'}，模式为${data.type}`);
                break;
            case BLUETHOOTH_SERVICE.SMART_LOCK_REMOVE:
                this.addEvent(IOT_EVENT_LEVEL.CODE_GREEN, '智能锁已删除认证记录，模式为' + data.type);
                break;
        }
    }
}

// 智能灯泡
export class SmartBulbDevice extends BluetoothDevice {
    // 开关 true: 开 false: 关
    protected power: boolean;

    // 颜色 rgba 颜色
    protected color: any;

    constructor(node: Node, power = false, color = "#ffff00") {
        super(node, generateRandomMac(), ["yopu.light"], ["制造商：友谱", "型号：YOPU-001", "版本：1.0.0"]);

        this.power = power;
        this.color = color;
    }

    get deviceType() {
        return DeviceType.SmartBulb;
    }

    getIndicatorOffset() {
        return {
            x: 27,
            y: 65,
        }
    }

    // 转换为JSON
    toJSON() {
        return {
            ...super.toJSON(),
            deviceType: DeviceType.SmartBulb,
            power: this.power,
            color: this.color,
        };
    }

    // 自JSON加载
    fromJSON(config: any): void {
        super.fromJSON(config);
        this.power = config.power;
        this.color = config.color;

        this.refreshGraph();
    }

    getValues() {
        return {
            power: this.power,
            color: this.color,
        }
    }

    // 计算显示颜色
    getDisplayColor() {
        // 如果关闭显示灰色
        if (!this.power) {
            return '#cccccc';
        }

        // 获取颜色和亮度
        return this.color;
    }

    // 刷新显示
    refreshGraph() {
        super.refreshGraph();

        // 刷新灯具色彩
        const color = this.getDisplayColor();
        const { node } = this;

        // 刷新灯泡颜色
        node.transition('attrs/bulb/fill', color, {
            delay: 0,
            duration: 1000,
        });

        // 刷新存储
        this.bridge.onGraphChange();
    }

    // 蓝牙设备需要实现的接口，接收控制指令
    HAL_Recv(fromInterface: IHALInterface, fromMAC: string, toMAC: string, type: HALFrameType, data: any, callback: Function): void {
        // 确定是蓝牙消息帧
        if (type !== HALFrameType.Bluetooth) {
            throw new Error('不是蓝牙消息帧');
        }

        // 根据服务类型处理
        switch(data.service) {
            // 设定智能灯泡
            case BLUETHOOTH_SERVICE.SMART_BULB_SET:
                // 智能灯泡
                this.power = data.power;
                this.color = (typeof data.color === 'string') ? data.color: data.color.hex;
                this.refreshGraph();
                callback();
                break;
            // 查询智能灯泡参数
            case BLUETHOOTH_SERVICE.SMART_BULB_QUERY:
                callback(this.getValues());
                break;
            default:
                throw new Error('未知的蓝牙服务' + data.service);
        }
    }

}

enum SMART_LOCK_AUTH_TYPE {
    FINGERPRINT = '指纹',
    PASSWORD = '密码',
    CARD = '卡片'
}

export enum SMART_LOCK_AUTH_MODE {
    REGISTER = '注册',
    VERIFY = '验证',
    REMOVE = '删除'
}

interface SMART_LOCK_AUTH_RECORD {
    type: SMART_LOCK_AUTH_TYPE;
    data: string;
}

// 智能门锁
export class SmartLockDevice extends BluetoothDevice {
    // 认证数据数组
    protected authRecords: SMART_LOCK_AUTH_RECORD[];

    // 门锁状态: true: 锁上 false: 锁开
    protected lockStatus: boolean;

    constructor(node: Node) {
        super(node, generateRandomMac(), [BLUETHOOTH_SERVICE.SMART_LOCK_LOCK, BLUETHOOTH_SERVICE.SMART_LOCK_UNLOCK, BLUETHOOTH_SERVICE.SMART_LOCK_REGISTER, BLUETHOOTH_SERVICE.SMART_LOCK_REMOVE], ["制造商：友谱", "型号：YOPU-003", "版本：1.0.0"]);

        this.authRecords = [{
            type: SMART_LOCK_AUTH_TYPE.PASSWORD,
            data: '888888',
        }];

        this.lockStatus = true;
    }

    get deviceType() {
        return DeviceType.SmartLock;
    }

    getIndicatorOffset() {
        return {
            x: 24,
            y: 44,
        }
    }

    // 注册认证，将认证数据添加到认证数据数组中
    // 参数：认证方式：指纹、密码、卡片
    // 参数：认证数据：指纹数据、密码、卡片号的字符串表示
    registerAuth(type: SMART_LOCK_AUTH_TYPE, data: string): void {
        this.authRecords.push({
            type,
            data
        });

        // 发送注册事件通知
        this.send(BLUETHOOTH_SERVICE.SMART_LOCK_REGISTER, { type, data });
    }

    // 验证认证，验证认证数据是否在认证数据数组中
    // 参数：认证方式：指纹、密码、卡片
    // 参数：认证数据：指纹数据、密码、卡片号的字符串表示
    // 返回值：是否验证通过
    verifyAuth(type: SMART_LOCK_AUTH_TYPE, data: string): boolean {
        // 获得验证结果
        const result = this.authRecords.some(record => record.type === type && record.data === data);

        // 发送验证事件通知
        this.send(BLUETHOOTH_SERVICE.SMART_LOCK_AUTH, { type, data, result });

        // 判断是否需要进行开关锁操作
        if (this.lockStatus !== result) {
            return result;
        }

        // 执行开关锁操作
        this.lockStatus = !result;

        if(result) {
            this.send(BLUETHOOTH_SERVICE.SMART_LOCK_UNLOCK);
        }
        else {
            this.send(BLUETHOOTH_SERVICE.SMART_LOCK_LOCK);
        }

        return result;
    }

    // 删除认证，删除认证数据数组中的认证数据
    // 参数：认证方式：指纹、密码、卡片
    // 参数：认证数据：指纹数据、密码、卡片号的字符串表示
    deleteAuth(type: SMART_LOCK_AUTH_TYPE, data: string): void {
        this.authRecords = this.authRecords.filter(record => record.type !== type || record.data !== data);

        if (this.authRecords.length <= 1) {
            throw new Error('至少保留一条认证数据');
        }  

        // 发送删除认证事件通知
        this.send(BLUETHOOTH_SERVICE.SMART_LOCK_REMOVE, { type, data });
    }

    // 转换为JSON
    toJSON() {
        return {
            ...super.toJSON(),
            deviceType: DeviceType.SmartLock,
            authRecords: this.authRecords,
            lockStatus: this.lockStatus
        };
    }

    // 自JSON加载
    fromJSON(config: any): void {
        super.fromJSON(config);
        this.authRecords = config.authRecords;
        this.lockStatus = config.lockStatus;
    }

    getValues() {
        return {
            authRecords: this.authRecords,
            lockStatus: this.lockStatus,
        }
    }

    // 蓝牙设备需要实现的接口，接收控制指令
    HAL_Recv(fromInterface: IHALInterface, fromMAC: string, toMAC: string, type: HALFrameType, data: any, callback: Function): void {
        // 接受消息实现强制开关锁
        if (type !== HALFrameType.Bluetooth) {
            throw new Error('不是蓝牙消息帧');
        }

        // 根据服务类型处理
        switch(data.service) {
            case BLUETHOOTH_SERVICE.SMART_LOCK_LOCK:
                // 锁上
                this.lockStatus = true;
                break;
            case BLUETHOOTH_SERVICE.SMART_LOCK_UNLOCK:
                // 锁开
                this.lockStatus = false;
                break;
            default:
                throw new Error('未知的蓝牙服务' + data.service);
        }
    }
}

// 智能门铃
export class SmartBellDevice extends BluetoothDevice {
    constructor(node: Node) {
        super(node, generateRandomMac(), [BLUETHOOTH_SERVICE.DOOR_BELL], ["制造商：友谱", "型号：YOPU-DOOR-BELL", "版本：1.0.0"]);
    }

    get deviceType() {
        return DeviceType.SmartBell;
    }

    // 按下门铃
    pressDoorBell() {
        // 发送蓝牙消息
        this.send(
            BLUETHOOTH_SERVICE.DOOR_BELL,
        );
    }

    // 转换为JSON
    toJSON() {
        return {
            ...super.toJSON(),
            deviceType: DeviceType.SmartBell,
        };
    }

    // 自JSON加载
    fromJSON(config: any): void {
        super.fromJSON(config);
    }
}

// 空调
export class AirConditionerDevice extends InfraredRayDevice {
    // 开关 true: 开 false: 关
    protected power: boolean;

    // 温度 16 ~ 30
    protected temperature: number;

    // 模式 制冷、制热、送风、除湿
    protected mode: string;

    // 设备名称
    protected deviceName: string;

    constructor(node: Node, power = false, temperature = 26, mode = '制冷', windSpeed = 15) {
        super(node);

        this.deviceName = '空调';
        this.power = power;
        this.temperature = temperature;
        this.mode = mode;
    }

    get deviceType() {
        return DeviceType.AirConditioner;
    }

    // 接收红外数据
    HAL_Recv(fromInterface: IHALInterface | null, fromMAC: string | null, toMAC: string | null, type: HALFrameType, data: any, callback: Function): void {
        // 非红外帧
        if (type !== HALFrameType.InfraredRay) {
            throw new Error('收到非红外帧');
        }

        // 红外帧的data是一个对象，包含了指令和参数
        switch (data.command) {
            case InfraredRayCommand.AirConditionerSetting:
                this.power = data.power;
                this.temperature = data.temperature;
                this.mode = data.mode;
                callback(data);
                this.refreshGraph();
                break;
            case InfraredRayCommand.AirConditionerQuery:
                callback(this.getValues());
                break;
            default:
                // 红外设备不处理未知的指令
                break;
        }
    }

    getValues() {
        return {
            power: this.power,
            temperature: this.temperature,
            mode: this.mode,
        }
    }

    // 转换为JSON
    toJSON() {
        return {
            ...super.toJSON(),
            deviceType: DeviceType.AirConditioner,
            power: this.power,
            temperature: this.temperature,
            mode: this.mode,
        };
    }

    // 自JSON加载
    fromJSON(config: any): void {
        super.fromJSON(config);
        this.power = config.power;
        this.temperature = config.temperature;
        this.mode = config.mode;
        this.refreshGraph();
    }

    // 刷新显示
    refreshGraph() {
        if(!this.power) {
            this.node.setAttrs({'image': { href: require('../../../assets/icon/airconditioner_off.png')}});
            this.node.setAttrs({'temperature': { text: '' }});
        } else {
            switch (this.mode) {
                case '制冷':
                    this.node.setAttrs({'image': { href: require('../../../assets/icon/airconditioner_cool.png')}});
                    break;
                case '制热':
                    this.node.setAttrs({'image': { href: require('../../../assets/icon/airconditioner_heat.png')}});
                    break;
                case '送风':
                    this.node.setAttrs({'image': { href: require('../../../assets/icon/airconditioner_wind.png')}});
                    break;
                case '除湿':
                    this.node.setAttrs({'image': { href: require('../../../assets/icon/airconditioner_dry.png')}});
                    break;
                default:
                    this.node.setAttrs({'image': { href: require('../../../assets/icon/airconditioner.png')}});
                    break;
            }

            if (this.temperature) {
                this.node.setAttrs({'temperature': { text: `${this.temperature}℃`}});
            }
        }

        // 刷新存储
        this.bridge.onGraphChange();
    }

}

// 投影机
export class ProjectorDevice extends InfraredRayDevice {
    protected power: boolean;

    // 设备名称
    protected deviceName: string;

    constructor(node: Node, power = false) {
        super(node);
        this.power = power;
        this.deviceName = '投影机';
    }

    get deviceType() {
        return DeviceType.Projector;
    }

    // 转换为JSON
    toJSON() {
        return {
            deviceType: DeviceType.Projector,
            power: this.power
        };
    }

    // 自JSON加载
    fromJSON(config: any): void {
        super.fromJSON(config);
        this.power = config.power;
        this.refreshGraph();
    }

    // 红外设备需要实现的接口，接收控制指令
    HAL_Recv(fromInterface: IHALInterface, fromMAC: string, toMAC: string, type: HALFrameType, data: any, callback: Function): void {
        // 非红外帧
        if (type !== HALFrameType.InfraredRay) {
            throw new Error('收到非红外帧');
        }

        // 红外帧的data是一个对象，包含了指令和参数
        switch (data.command) {
            case InfraredRayCommand.ProjectorDevicePowerOn:
                if(!this.power) {
                    this.power = true;
                    this.refreshGraph();
                }
                callback(this.power);
                break;
            case InfraredRayCommand.ProjectorDevicePowerOff:
                if(this.power) {
                    this.power = false;
                    this.refreshGraph();
                }
                callback(this.power);
                break;
            default:
                // 红外设备不处理未知的指令
                break;
        }
    }

    // 刷新显示
    refreshGraph() {
        if(this.power) {
            this.node.setAttrs({'image': { href: require('../../../assets/icon/projector_on.png')}});
        } else {
            this.node.setAttrs({'image': { href: require('../../../assets/icon/projector.png')}});
        }

        // 刷新存储
        this.bridge.onGraphChange();
    }
}

// 投影幕布
export class ScreenDevice extends InfraredRayDevice {
    protected power: boolean;

    // 设备名称
    protected deviceName: string;

    constructor(node: Node, power = false) {
        super(node);
        this.power = power;
        this.deviceName = '投影幕布';
    }

    get deviceType() {
        return DeviceType.Screen;
    }

    // 红外设备需要实现的接口，接收控制指令
    HAL_Recv(fromInterface: IHALInterface, fromMAC: string, toMAC: string, type: HALFrameType, data: any, callback: Function): void {
        // 非红外帧
        if (type !== HALFrameType.InfraredRay) {
            throw new Error('收到非红外帧');
        }

        // 红外帧的data是一个对象，包含了指令和参数
        switch (data.command) {
            case InfraredRayCommand.ScreenDevicePowerOn:
                if(!this.power) {
                    this.power = true;
                    this.refreshGraph();
                }

                callback(this.power);
                break;
            case InfraredRayCommand.ScreenDevicePowerOff:
                if(this.power) {
                    this.power = false
                    this.refreshGraph();
                }

                callback(this.power);
                break;
            default:
                // 红外设备不处理未知的指令
                break;
        }
    }

    // 转换为JSON
    toJSON() {
        return {
            deviceType: DeviceType.Screen,
            power: this.power
        };
    }

    // 自JSON加载
    fromJSON(config: any): void {
        super.fromJSON(config);
        this.power = config.power;
        this.refreshGraph();
    }

    // 刷新显示
    refreshGraph() {
        if(this.power) {
            this.node.setAttrs({'image': { href: require('../../../assets/icon/screen_on.png')}});
        } else {
            this.node.setAttrs({'image': { href: require('../../../assets/icon/screen_off.png')}});
        }

        // 刷新存储
        this.bridge.onGraphChange();
    }

}